import { MicIcon, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AudioLines } from "~/components/animate-ui/icons/audio-lines";
import { AppLogo } from "~/components/AppLogo";
import { PageLayout } from "~/components/PageLayout";
import { Button } from "~/components/ui/button";
import { Modal } from "~/components/ui/modal";
import { SKY_LAST_TRANSCRIBED_TEXT } from "~/constants/index";
import { NoteEditor } from "~/features/notes/components/NoteEditor";
import {
  AudioRecordingButton,
  AudioRecordingButtonHandle,
} from "~/features/transcribing/AudioRecordingButton";
import { useTranscribing } from "~/features/transcribing/hooks/useTranscribing";
import { ROUTES } from "~/helpers";
import { toast } from "~/hooks/use-toast";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import "./TranscribingPage.css";

export const TranscribingPage = () => {
  useTrackPageVisit("app/transcribing/visit");

  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const {
    isProcessingQueue,
    handleAutoImproveChunk,
    textTranscribing,
    resetTranscribing,
    updateTextTranscribing,
  } = useTranscribing();

  const [lastSpokenText, setLastSpokenText] = useState<string>("");
  const [spokenBeforeImprovement, setSpokenBeforeImprovement] =
    useState<string>("");

  const transcribingContainerRef = useRef<HTMLDivElement>(null);
  const recordingButtonRef = useRef<AudioRecordingButtonHandle>(null);
  const [isRecordingStop, setIsRecordingStop] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);

  // Auto-scroll to bottom when transcribing and recording, scroll to top when recording stops
  useEffect(() => {
    if (transcribingContainerRef.current) {
      if ((textTranscribing || lastSpokenText) && isRecordingActive) {
        setTimeout(() => {
          if (transcribingContainerRef.current) {
            transcribingContainerRef.current.scrollTop =
              transcribingContainerRef.current.scrollHeight;

            const noteEditorContainer =
              transcribingContainerRef.current.querySelector(
                '[class*="overflow-y-auto"]'
              );
            if (noteEditorContainer) {
              noteEditorContainer.scrollTop = noteEditorContainer.scrollHeight;
            }
          }
        }, 10);
      } else if (
        !isRecordingActive &&
        (textTranscribing || lastSpokenText || spokenBeforeImprovement)
      ) {
        // Scroll to top when recording stops and there's content
        setTimeout(() => {
          if (transcribingContainerRef.current) {
            transcribingContainerRef.current.scrollTop = 0;

            const noteEditorContainer =
              transcribingContainerRef.current.querySelector(
                '[class*="overflow-y-auto"]'
              );
            if (noteEditorContainer) {
              noteEditorContainer.scrollTop = 0;
            }
          }
        }, 10);
      }
    }
  }, [
    textTranscribing,
    isRecordingActive,
    lastSpokenText,
    spokenBeforeImprovement,
  ]);

  const onAutoImproveChunk = async (chunk: string) => {
    if (!spokenBeforeImprovement.length) {
      setSpokenBeforeImprovement(chunk);
    }
    setLastSpokenText("");

    const errorMessage = await handleAutoImproveChunk(chunk);
    if (errorMessage) {
      toast({
        variant: "default",
        title: "Auto Improvement Failed",
        description: errorMessage,
      });
    } else {
      setSpokenBeforeImprovement("");
    }
  };

  const onSpeechUpdate = (text: string) => {
    setLastSpokenText(text);
  };

  const onRecordingStop = async () => {
    // Called when recording stops; enable the stopped UI if there's transcribed text
    if (textTranscribing && textTranscribing.length) {
      setIsRecordingStop(true);
    }
  };

  useEffect(() => {
    const savedText = localStorage.getItem(SKY_LAST_TRANSCRIBED_TEXT);
    if (savedText) {
      updateTextTranscribing(savedText);
    }
  }, [updateTextTranscribing]);

  useEffect(() => {
    if (!isRecordingActive && textTranscribing && textTranscribing.length) {
      setIsRecordingStop(true);
      localStorage.setItem(SKY_LAST_TRANSCRIBED_TEXT, textTranscribing);
    }
  }, [isRecordingActive, textTranscribing]);

  const onRecordReset = () => {
    setShowStartOverModal(false);
    setIsRecordingStop(false);
    setLastSpokenText("");
    setSpokenBeforeImprovement("");
    resetTranscribing();
    localStorage.removeItem(SKY_LAST_TRANSCRIBED_TEXT);
  };

  return (
    <PageLayout fullWidth>
      <div className="pt-[30px] w-full h-full min-h-[100vh] flex flex-col overflow-hidden">
        <div className="flex justify-center align-middle relative">
          <AppLogo />
        </div>
        <div className="relative">
          {!isRecordingActive && (
            <div
              className="absolute top-auto bottom-[-1px] z-10 h-[300px] inset-0 flex-shrink-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(1, 1, 1, 0.00) 0%, #fff 101.42%)",
              }}
            />
          )}
          {(textTranscribing && textTranscribing.length) ||
          (spokenBeforeImprovement && spokenBeforeImprovement.length) ||
          (lastSpokenText && lastSpokenText.length) ? (
            <div
              ref={transcribingContainerRef}
              className="text-3xl mt-6 text-[#1E293B] w-[100%] mt-[19px] mx-auto max-w-[720px] max-h-[calc(100vh-345px)] overflow-y-auto scrollbar-hide"
            >
              {isRecordingActive && (
                <div
                  className="absolute z-10 h-[120px] inset-0 flex-shrink-0"
                  style={{
                    background:
                      "linear-gradient(0deg, rgba(1, 1, 1, 0.00) 0%, #fff 101.42%)",
                  }}
                />
              )}
              <NoteEditor
                onChange={() => {}}
                content={
                  textTranscribing + spokenBeforeImprovement + lastSpokenText
                }
                readOnly={true}
                className="h-full max-h-none scrollbar-hide overflow-y-auto overflow-x-hidden"
                textSize="2xl"
                textSizeMobile="lg"
                textColor="#0F172A"
              />
            </div>
          ) : isRecordingActive ? (
            <div className="sm:text-3xl text-[18px] mt-6 text-[#94A3B8] mt-[154px] mx-auto  sm:max-w-[720px] max-w-[370px] flex items-center sm:px-0 px-[16px]">
              Listening... Just keep speaking... <AudioLines animate />
            </div>
          ) : (
            <div className="sm:text-3xl text-[18px] mt-6 text-[#94A3B8] mt-[154px] mx-auto  sm:max-w-[720px] max-w-[370px] sm:px-0 px-[16px] z-10 relative">
              Just press space or button below and start speaking, the text is
              transcribed automatically.
            </div>
          )}
        </div>

        {textTranscribing && isRecordingStop && (
          <div className="pb-[40px] text-sm text-[#475569] mt-[auto] mb-2 mx-auto flex flex-col items-center gap-[16px]">
            <p className="text-center text-[20px] leading-[24px] font-[500] sm:text-[32px] sm:leading-[40px] text-onest">
              Turn voice into
              <br />
              smart medical case
            </p>
            <Button
              size="sm"
              className="py-[16px] text-[14px] px-[20px] sm:h-[48px] h-[40px] rounded-[16px]"
            >
              <Link to={ROUTES.REGISTER}>Convert Text to Case</Link>
            </Button>

            <div className="mt-[8px] flex gap-[8px]">
              <div
                onClick={() => {
                  setIsRecordingStop(false);
                  setTimeout(() => {
                    void recordingButtonRef.current?.startRecording();
                  }, 0);
                }}
                className="flex items-center border border-[#E2E8F0] py-[16px] px-[24px] rounded-[16px] h-[48px] text-[#2563EB] gap-[8px] cursor-pointer hover:bg-[#F0F9FF]"
              >
                <MicIcon className="w-4 h-4" />
                <p className="text-sm">Speak More</p>
              </div>
              <div
                onClick={() => setShowStartOverModal(true)}
                className="flex items-center border border-[#E2E8F0] py-[16px] px-[24px] rounded-[16px] h-[48px] text-[#2563EB] gap-[8px] cursor-pointer hover:bg-[#F0F9FF]"
              >
                <RotateCcw className="w-4 h-4" />
                <p className="text-sm">Start over</p>
              </div>
            </div>
          </div>
        )}

        {!isRecordingStop && (
          <AudioRecordingButton
            ref={recordingButtonRef}
            className="mt-auto"
            onRecordingStateChange={setIsRecordingActive}
            isProcessingImprovement={isProcessingQueue}
            isLoading={isProcessingQueue}
            onAutoImproveChunk={onAutoImproveChunk}
            onRecordingStop={onRecordingStop}
            isProcessingQueue={isProcessingQueue}
            onAudioUpload={() => {}}
            onTextSpeechUpdate={onSpeechUpdate}
          />
        )}
      </div>

      <Modal
        open={showStartOverModal}
        onOpenChange={setShowStartOverModal}
        title="Start Over"
        description={
          <>
            Are you sure you want to start recording from the beginning?
            <br />
            Current data will not be saved.
          </>
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setShowStartOverModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={onRecordReset}>Start Over</Button>
          </>
        }
      />
    </PageLayout>
  );
};
